// Framework-free orchestration + message helpers for cancelling a booking.
// Kept free of React/Redux imports so the logic stays unit-testable.

import fetchJson from 'react-admin-loopback/lib/fetch'
import { config } from '../config'

export function cancelResultMessage(resultOrError) {
  if (resultOrError instanceof Error) {
    return {
      text: 'Annuleren mislukt, de boeking is niet geannuleerd',
      type: 'warning',
    }
  }

  if (resultOrError && resultOrError.externalCancelFailed) {
    return {
      text:
        'Boeking geannuleerd, maar de reservering bij de club kon niet automatisch worden verwijderd. Ruim die handmatig op',
      type: 'warning',
    }
  }

  if (resultOrError && resultOrError.externalCancelled) {
    return {
      text: 'Boeking geannuleerd (ook extern verwijderd)',
      type: 'info',
    }
  }

  return {
    text: 'Lokaal geannuleerd — geen externe reservering om op te ruimen',
    type: 'info',
  }
}

async function isCancelled(dataProvider, id) {
  try {
    const { data } = await dataProvider.getOne('booking', { id })
    return !!(data && data.cancelled)
  } catch (error) {
    return false
  }
}

export async function performCancel({
  dataProvider,
  booking,
  alsoRefund,
  httpClient = fetchJson,
}) {
  // The dataProvider's CREATE discards the response body (react-admin-loopback
  // returns only { id }), which swallowed externalCancelled and made every
  // cancel report "geen externe reservering om op te ruimen" (#10). Call the
  // endpoint through the authenticated fetch wrapper so the real body is read.
  let result
  try {
    const cancelResp = await httpClient(
      `${config.apiUrl}/booking/${booking.id}/cancel`,
      { method: 'POST', body: '{}' }
    )
    result = cancelResp.json || {}
  } catch (error) {
    // De API annuleert eerst lokaal en geeft daarna een 502 als de reservering
    // bij de club niet weg kan (Slack krijgt dan booking_cancel_failed). In
    // productie strip strong-error-handler de 5xx-body, dus de error-code is
    // hier niet te zien: haal de boeking opnieuw op. Is die geannuleerd, ga dan
    // gewoon door met refund en mail, anders blijft het geld stilletjes staan.
    const cancelledLocally = await isCancelled(dataProvider, booking.id)
    if (!cancelledLocally) throw error
    result = {
      cancelled: true,
      externalCancelled: false,
      externalCancelFailed: true,
    }
  }

  // Distinguish "no refund attempted" (skipped) from "refund attempted but
  // failed" so the caller only warns on an actual failure, not on a skip.
  let refunded = false
  let refundSkipped = true
  let refundFailed = false
  const paymentData = booking.payment_data || {}
  const paymentId = paymentData.payment_id
  const creditPurchaseId = paymentData.creditcard_purchase_id
  if (alsoRefund && (paymentId || creditPurchaseId)) {
    refundSkipped = false
    try {
      if (paymentId) {
        await dataProvider.create('payment/' + paymentId + '/refund', {
          data: { id: paymentId, noRefundCosts: true },
        })
      } else {
        // Strip booking: give the credit back via the same endpoint the
        // strippenkaart page uses; it guards against double returns itself.
        await dataProvider.create(
          'creditcard/' + paymentData.creditcard_id + '/return',
          {
            data: {
              participantId: booking.participant_id,
              bookingId: booking.id,
              purchaseId: creditPurchaseId,
            },
          }
        )
      }
      refunded = true
    } catch (error) {
      refundFailed = true
    }
  }

  // Annuleringsmail met het werkelijk uitgevoerde scenario (#13). Na een
  // mislukte terugboeking géén mail: de admin handelt dat handmatig af en de
  // mail zou anders een terugbetaling beloven die niet is gebeurd.
  let emailSent = false
  let emailFailed = false
  if (!refundFailed) {
    const refundScenario = refunded ? (paymentId ? 'refund' : 'strip') : 'none'
    try {
      const emailResp = await httpClient(
        `${config.apiUrl}/booking/${booking.id}/sendCancellationEmail`,
        { method: 'POST', body: JSON.stringify({ refundScenario }) }
      )
      emailSent = !!(emailResp.json && emailResp.json.success)
      emailFailed = !emailSent
    } catch (error) {
      emailFailed = true
    }
  }

  return {
    ...result,
    refunded,
    refundSkipped,
    refundFailed,
    emailSent,
    emailFailed,
  }
}
